import { DatePicker, Image, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import Avatar from "../../../../assets/images/avatar.svg";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetTopPerformersQuery } from "../../../../features/superAdmin/dashboard/dashboardApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import { countParPageSerialNumber } from "../../../../utils/number";
import { DFilters } from "../Dashboard";

const { RangePicker } = DatePicker;
type RangeValue<T> = [EventValue<T>, EventValue<T>] | null;
type EventValue<T> = T | null;

export default function TopPerformers({
  fromDate,
  toDate,
  limit: l,
  skip: s,
  ...filters
}: DFilters) {
  const { filter, handleFilter, page, limit } = useTable<DFilters>();

  const {
    data: performers,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetTopPerformersQuery({
    params: {
      ...filters,
      fromDate: filter?.fromDate || fromDate || undefined,
      toDate: filter?.toDate || toDate || undefined,
    },
  });

  useError(isError, error, "Top performers get fail");

  useEffect(() => {
    if (toDate && fromDate) {
      // Reset filters
      handleFilter("fromDate", undefined);
      handleFilter("toDate", undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toDate, fromDate]);

  const columns: ColumnsType<{
    id: number;
    userName: string;
    photo: string | null;
    role: string;
    numberOfTicketCompletion: number;
    averageTicketCompletionTime: string;
  }> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee Name",
      render: (val) => {
        return (
          <Space>
            <Image
              src={val?.photo || Avatar}
              width={40}
              height={40}
              preview={false}
              className="rounded-full !object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = Avatar;
              }}
            />
            <h6 className="text-sm text-display-body font-medium">
              {val?.userName}
            </h6>
          </Space>
        );
      },
    },
    {
      key: "ticket-approved",
      dataIndex: "numberOfTicketCompletion",
      align: "center",
      title: <span className="text-nowrap">Ticket Resolved</span>,
      render: (val) => (val ? val : "N/A"),
    },
    {
      key: "Avg",
      dataIndex: "averageTicketCompletionTime",
      align: "center",
      title: <span className="text-nowrap">Avg. ticket resolution time </span>,
      render: (val) => (val ? <span>{val} min</span> : "N/A"),
    },
  ];

  return (
    <CustomTable
      total={5}
      page={page}
      limit={limit}
      columns={columns}
      hideRowSelect
      hidePagination
      className="designationTable"
      topSectionClassName="w-full"
      rootClassName="border-none shadow !rounded-xl"
      dataSource={performers?.data?.slice(0, 5)}
      loading={isLoading || isFetching}
      filterSection={
        <div className="flex justify-between items-center w-full">
          <h2 className="text-xl text-header font-medium">
            Top 5 Ticket Performers
          </h2>
          <RangePicker
            className="max-w-64 !h-10"
            value={
              filter.fromDate && filter.toDate
                ? [dayjs(filter.fromDate), dayjs(filter.toDate)]
                : null
            }
            onChange={(dates: RangeValue<Dayjs>) => {
              if (dates && dates[0] && dates[1]) {
                handleFilter("fromDate", dates[0].format("YYYY-MM-DD"));
                handleFilter("toDate", dates[1].format("YYYY-MM-DD"));
              } else {
                handleFilter("fromDate", undefined);
                handleFilter("toDate", undefined);
              }
            }}
          />
        </div>
      }
    />
  );
}
